import * as React from 'react';
import { Platform, ScrollView, type ScrollViewProps } from 'react-native';

type SmoothWebScrollViewProps = ScrollViewProps & {
  smoothWebWheel?: boolean;
};

type WebScrollViewRef = ScrollView & {
  getScrollableNode?: () => Element | null;
};

function normalizeWheelDelta(event: WheelEvent) {
  if (event.deltaMode === WheelEvent.DOM_DELTA_LINE) {
    return event.deltaY * 16;
  }
  if (event.deltaMode === WheelEvent.DOM_DELTA_PAGE) {
    return event.deltaY * window.innerHeight;
  }
  return event.deltaY;
}

function shouldIgnoreWheel(event: WheelEvent) {
  if (
    event.defaultPrevented ||
    Math.abs(event.deltaY) <= Math.abs(event.deltaX) ||
    event.deltaY === 0 ||
    event.ctrlKey ||
    event.metaKey
  ) {
    return true;
  }

  const target = event.target;
  if (!(target instanceof Element)) {
    return false;
  }

  return Boolean(
    target.closest(
      'input, textarea, select, [contenteditable="true"], [role="menu"], [role="listbox"], [data-radious-ignore-smooth-scroll]'
    )
  );
}

export const SmoothWebScrollView = React.forwardRef<ScrollView, SmoothWebScrollViewProps>(
  function SmoothWebScrollView(
    { onScroll, scrollEventThrottle = 16, smoothWebWheel = true, ...props },
    forwardedRef
  ) {
    const [scrollElement, setScrollElement] = React.useState<HTMLElement | null>(null);
    const offsetRef = React.useRef(0);

    const setRef = React.useCallback(
      (node: ScrollView | null) => {
        const nextScrollElement =
          Platform.OS === 'web'
            ? (node as WebScrollViewRef | null)?.getScrollableNode?.()
            : null;
        setScrollElement(
          nextScrollElement instanceof HTMLElement ? nextScrollElement : null
        );
        if (typeof forwardedRef === 'function') {
          forwardedRef(node);
          return;
        }
        if (forwardedRef) {
          forwardedRef.current = node;
        }
      },
      [forwardedRef]
    );

    React.useEffect(() => {
      if (Platform.OS !== 'web' || !smoothWebWheel || !scrollElement) {
        return;
      }

      const handleWheel = (event: WheelEvent) => {
        if (shouldIgnoreWheel(event)) {
          return;
        }

        event.preventDefault();
        const maxOffset = Math.max(0, scrollElement.scrollHeight - scrollElement.clientHeight);
        const nextOffset = Math.min(
          maxOffset,
          Math.max(0, offsetRef.current + normalizeWheelDelta(event))
        );
        offsetRef.current = nextOffset;
        scrollElement.scrollTop = nextOffset;
      };

      // react-doctor-disable-next-line react-doctor/client-passive-event-listeners
      scrollElement.addEventListener('wheel', handleWheel, { passive: false });

      return () => {
        scrollElement.removeEventListener('wheel', handleWheel);
      };
    }, [scrollElement, smoothWebWheel]);

    return (
      <ScrollView
        {...props}
        ref={setRef}
        onScroll={(event) => {
          if (Platform.OS === 'web') {
            offsetRef.current = event.nativeEvent.contentOffset.y;
          }
          onScroll?.(event);
        }}
        scrollEventThrottle={scrollEventThrottle}
      />
    );
  }
);
